import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";

const TILES = [
  { key: "clothing", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_heda-luxury/artifacts/icu0z7g1_clothes%20front.webp" },
  { key: "bags", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_heda-luxury/artifacts/jxs5dpuh_bags%20front.webp" },
  { key: "shoes", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_heda-luxury/artifacts/ly43w764_shoes%20front.webp" },
  { key: "jewellery", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_heda-luxury/artifacts/d9plkusy_jewelry%20front.png" },
];

export default function Hero() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/catalogue?q=${encodeURIComponent(term)}` : "/catalogue");
  };

  return (
    <section className="pt-28 md:pt-36 px-5 md:px-10" data-testid="hero">
      {/* Intro + search */}
      <div className="max-w-3xl">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.9 }}
          className="text-xs uppercase tracking-[0.28em] text-muted mb-6"
        >
          <span className="text-[var(--gold-deep)] font-medium">Heda Gelogaeva</span>
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
        >
          {t.home.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 1 }}
          className="text-base md:text-lg font-light text-ink/60 mt-6"
        >
          {t.home.intro}
        </motion.p>

        <motion.form
          onSubmit={onSearch}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 1 }}
          className="mt-8 flex items-center border border-ink/80 focus-within:border-ink transition-colors"
          data-testid="home-search-form"
        >
          <Search className="h-5 w-5 ml-4 text-ink/50" strokeWidth={1.4} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.home.searchPlaceholder}
            data-testid="home-search-input"
            className="flex-1 bg-transparent px-4 py-4 text-base font-light focus:outline-none placeholder:text-ink/40"
          />
          <button
            type="submit"
            data-testid="home-search-submit"
            className="bg-ink text-paper px-6 md:px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink self-stretch"
          >
            {t.home.search}
          </button>
        </motion.form>
      </div>

      {/* Category tiles */}
      <Reveal delay={0.1} className="mt-8 md:mt-20">
        <p className="text-xs uppercase tracking-[0.24em] text-muted mb-4 md:mb-6">{t.home.browse}</p>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        {TILES.map((tile, i) => (
          <motion.div
            key={tile.key}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: i * 0.08, ease: [0.19, 1, 0.22, 1] }}
          >
            <Link
              to={`/catalogue?category=${tile.key}`}
              data-testid={`category-tile-${tile.key}`}
              className="group relative block overflow-hidden bg-surface aspect-[3/4]"
            >
              <img
                src={tile.img}
                alt={t.home.tiles[tile.key]}
                className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                <span className="font-display text-2xl md:text-3xl text-paper">{t.home.tiles[tile.key]}</span>
                <span className="text-paper text-[10px] uppercase tracking-[0.2em] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                  {t.hero.cta1}
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
